import * as React from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

import { ToastProps } from '../../hooks/use-toast';

type ToastVariant = 'default' | 'destructive' | 'success' | 'info' | 'error';

interface ToastComponentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Omit<ToastProps, 'id'> {
  id: string;
  onClose?: () => void;
  variant?: ToastVariant;
}

const Toast = React.forwardRef<HTMLDivElement, ToastComponentProps>(
  (
    {
      className,
      variant = 'info',
      title,
      description,
      action,
      onClose,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'group pointer-events-auto relative flex w-full flex-col items-start overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all',
          variant === 'default' && 'border-blue-500 bg-blue-500 text-white',
          variant === 'info' && 'border-blue-500 bg-blue-500 text-white',
          variant === 'destructive' && 'border-red-500 bg-red-500 text-white',
          variant === 'error' && 'border-red-500 bg-red-500 text-white',
          variant === 'success' && 'border-green-500 bg-green-500 text-white',
          className
        )}
        {...props}
      >
        <div className="flex w-full flex-col gap-1">
          {title && <div className="text-sm font-medium mb-1">{title}</div>}
          {description && (
            <div
              className="text-sm opacity-90 max-h-[300px] overflow-y-auto pr-2 whitespace-pre-line break-words"
              style={{ scrollbarWidth: 'thin' }}
            >
              {description}
            </div>
          )}
          {action && <div className="mt-2">{action}</div>}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className="absolute right-2 top-2 rounded-md p-1 text-white/70 opacity-100 transition-opacity hover:text-white focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);
Toast.displayName = 'Toast';

const ToastTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={clsx('text-sm font-medium', className)} {...props} />
));
ToastTitle.displayName = 'ToastTitle';

const ToastDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={clsx('text-sm opacity-90', className)} {...props} />
));
ToastDescription.displayName = 'ToastDescription';

type ToastProviderProps = React.PropsWithChildren & {
  toasts: ToastProps[];
  onClose: (id: string) => void;
};

const ToastProvider = ({ toasts, onClose, children }: ToastProviderProps) => {
  return (
    <>
      {children}
      <div className="fixed top-0 z-[100] flex flex-col items-end gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col-reverse md:max-w-[600px]">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            description={toast.description}
            action={toast.action}
            variant={(toast.variant as ToastVariant) || 'info'}
            onClose={() => onClose(toast.id)}
          />
        ))}
      </div>
    </>
  );
};

export { Toast, ToastTitle, ToastDescription, ToastProvider };
